import { useForm } from 'react-hook-form'

import {
  IncidentQueryResult,
  useFacilityGuardsQuery,
} from '@/graphql/generated/hooks'
import { getGuardOptions } from '@/utils/forms/optionBuilders'
import { getOptionsArray } from '@/utils/forms/optionHelpers'

import { GuardsFormInputs } from '../types/guardActionTypes'
import { isSpecificGuardOption } from '../utils/guards'

interface UseGuardsIProps {
  incident?: IncidentQueryResult['data']['incident']
}

export const useGuards = ({ incident }: UseGuardsIProps) => {
  const facilityId = incident?.facility?.id
  const alreadyDispatchedGuardIds = incident?.dispatchedGuardIds || []

  const { data: guardsData, loading: isGuardsLoading } = useFacilityGuardsQuery(
    {
      variables: { facilityId },
      fetchPolicy: 'network-only',
    }
  )

  const {
    control,
    watch,
    handleSubmit,
    errors,
    formState: { isValid },
  } = useForm<GuardsFormInputs>({
    mode: 'onChange',
  })

  // Guards must be explicitly selected — there is no "All guards" default
  // (ENG-32146). `formState.isValid` only settles after the first interaction,
  // so the watched selection is what keeps Dispatch disabled on first render.
  const hasSelectedGuards =
    getOptionsArray(watch('guards')).filter(isSpecificGuardOption).length > 0

  const guards = guardsData?.checkedInUsers?.edges.map((edge) => edge.node)

  // Guards that have already been dispatched by a specific dispatch should be removed from options
  const guardsOptions = getGuardOptions(guardsData).filter(
    (option) => !alreadyDispatchedGuardIds.includes(option.value)
  )

  return {
    control,
    guards,
    guardsErrorMessage: !!errors?.guards && 'Guards are required',
    guardsOptions,
    handleSubmit,
    hasSelectedGuards,
    isGuardsLoading,
    isGuardsValid: isValid,
  }
}
